import { useEffect, useMemo, useRef } from 'react';

import { createPortal } from 'react-dom';

import BubbleLoader from '../Loaders/BubbleLoader';

import cross from './assets/cross.svg';

import './styles.scss';

/**
 * Accessible modal dialog rendered in a portal on `document.body`. It traps focus, closes on
 * Escape or a backdrop click, and restores focus to the previously focused element on unmount.
 *
 * @param {object} props - Component props.
 * @param {string} props.title - Heading text shown in the modal header.
 * @param {string} props.content - HTML string injected into the modal body.
 * @param {boolean} [props.loading] - Shows a loader in the body instead of `content` while the text loads.
 * @param {() => void} [props.onClose] - Called when the user closes the modal by the close button, Escape or the backdrop.
 * @param {boolean} [props.noImg] - Adds the body modifier class used for content without images; defaults to false.
 * @param {boolean} [props.noTitle] - Adds the body modifier class used when there is no title; defaults to false.
 * @param {boolean} [props.isAntiClose] - Makes the modal impossible to dismiss: hides the close button and ignores Escape and backdrop clicks; defaults to false.
 * @returns {import("react").ReactPortal} - The modal markup rendered into the portal element.
 */
const InfoModal = ({
    title,
    content,
    loading,
    onClose,
    noImg = false,
    noTitle = false,
    isAntiClose = false,
}) => {
    const portalEl = useMemo(() => {
        const div = document.createElement('div');
        div.classList.add('info-modal');
        div.setAttribute('data-modal-portal', 'true');
        return div;
    }, []);

    const dialogRef = useRef(null);
    const lastActiveElRef = useRef(null);

    useEffect(() => {
        document.body.appendChild(portalEl);
        document.body.classList.add('info-modal-is-open');

        lastActiveElRef.current = document.activeElement;
        queueMicrotask(() => dialogRef.current?.focus());

        return () => {
            if (portalEl.parentNode) portalEl.parentNode.removeChild(portalEl);
            document.body.classList.remove('info-modal-is-open');

            const last = lastActiveElRef.current;
            if (last && typeof last.focus === 'function') last.focus();
        };
    }, [portalEl]);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const isClosable = !isAntiClose;

        const getFocusable = () => {
            const nodes = dialog.querySelectorAll(
                'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
            );
            return Array.from(nodes).filter(
                (el) => el && el.offsetParent !== null && !el.hasAttribute('disabled')
            );
        };

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (!isClosable) return;
                e.preventDefault();
                onClose?.();
                return;
            }

            if (e.key !== 'Tab') return;

            const focusable = getFocusable();
            if (focusable.length === 0) {
                e.preventDefault();
                dialog.focus();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;

            if (e.shiftKey) {
                if (active === first || active === dialog) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (active === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isAntiClose, onClose]);

    const onBackdropMouseDown = (e) => {
        if (isAntiClose) return;
        if (e.target === e.currentTarget) onClose?.();
    };

    return createPortal(
        <div className='info-modal__backdrop' onMouseDown={onBackdropMouseDown} aria-hidden={false}>
            <div
                className='info-modal__dialog info-modal__dialog--scrollable'
                role='dialog'
                aria-modal='true'
                aria-labelledby='info-modal-title'
                tabIndex={-1}
                ref={dialogRef}>
                <div className='info-modal__content'>
                    <div className='info-modal__header'>
                        <div className='info-modal__title' id='info-modal-title'>
                            {title}
                        </div>
                        {!isAntiClose && (
                            <button
                                type='button'
                                className='info-modal__close'
                                onClick={onClose}
                                aria-label='Close'>
                                <img
                                    src={cross}
                                    alt='#'
                                    aria-hidden='true'
                                    className='info-modal__close-icon'
                                />
                            </button>
                        )}
                    </div>
                    <div
                        className={`info-modal__body ${noTitle ? 'info-modal__body--no-title' : ''} ${
                            noImg ? 'info-modal__body--no-img' : ''
                        } ${isAntiClose ? 'info-modal__body--no-close' : ''}`}>
                        {loading ? (
                            <BubbleLoader
                                backgroundColor={'transperent'}
                                loaderColor={'#011A4C'}
                                isOverlay={false}
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: content }} />
                        )}
                    </div>
                </div>
            </div>
        </div>,
        portalEl
    );
};

export default InfoModal;
